import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useSearchPromosByToggle } from '~/hooks/apis/promo/useSearchPromosByToggle';
import useCurrentPromoBrand from '~/hooks/useCurrentPromoBrand';
import useDebounced from '~/hooks/useDebounced';

import type { PromoMeta, SearchPromosByToggleQuery, SearchPromosByToggleResponse } from '~/services/PromoService';

export type PromoByToggleSearchResults = SearchPromosByToggleResponse['result'];
export type PromoByToggleEntity = SearchPromosByToggleQuery['entity'];
export type PromoByToggleDisplayMode = 'standard' | 'refined';
type Toggle = SearchPromosByToggleQuery['toggle'];
export type ToggleSearchRelatedFeature = Extract<SearchPromosByToggleQuery['is-related-to'], string>;

export const toggleSearchRelatedFeatures = {
  promo: [
    { value: 'promo-display-config', label: 'Promo Display' },
    { value: 'marketing-router', label: 'Marketing Router' },
  ],
  voucher: [
    { value: 'voucher-display-config', label: 'Voucher Display V1' },
    { value: 'marketing-router', label: 'Marketing Router' },
    { value: 'voucher-auto-assignment-rules', label: 'Voucher auto-assignment' },
  ],
} as const satisfies Record<PromoByToggleEntity, ReadonlyArray<{ value: ToggleSearchRelatedFeature; label: string }>>;

/** The complete, presentation-independent value produced by a toggle lookup. */
export type PromoByToggleSearch = {
  entity: PromoByToggleEntity;
  results: PromoByToggleSearchResults;
  displayMode: PromoByToggleDisplayMode;
};

type LockedNameProps =
  | { codeName?: never; voucherName?: never }
  | {
      /** Locks the lookup to this promo code name and hides the editable name and entity controls. */
      codeName: string;
      voucherName?: never;
    }
  | {
      /** Locks the lookup to this voucher name and hides the editable name and entity controls. */
      codeName?: never;
      voucherName: string;
    };

type Props = {
  promoMeta?: PromoMeta | null;
  /** Prevents a lookup until the caller makes this control active. */
  isActive?: boolean;
  /** Forces the related-feature request parameter and hides the optional checkbox. */
  includeRelatedFeaturesSet?: boolean;
  /** Fixes the results presentation and hides the display-mode selector. */
  displayModeSet?: PromoByToggleDisplayMode;
  /** Fixes the entity and hides the entity/toggle selectors for lookup-only use. */
  modeSet?: PromoByToggleEntity;
  /** Fixes the toggle and hides the entity/toggle selectors for lookup-only use. */
  toggleSet?: Toggle;
  /** Receives each completed lookup, or undefined while the lookup is changed or disabled. */
  onSearchChange?: (search?: PromoByToggleSearch) => void;
} & LockedNameProps;

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unable to search by toggle.';
}

const NAME_QUERY_DEBOUNCE_MS = 300;

export default function PromoByToggle({
  promoMeta,
  codeName,
  voucherName,
  isActive = true,
  includeRelatedFeaturesSet,
  displayModeSet,
  modeSet,
  toggleSet,
  onSearchChange,
}: Props) {
  const { promoBrand: brand } = useCurrentPromoBrand();
  const [entity, setEntity] = useState<PromoByToggleEntity>('promo');
  const [toggle, setToggle] = useState<Toggle>();
  const [query, setQuery] = useState('');
  const [hasFocusedNameQuery, setHasFocusedNameQuery] = useState(false);
  const [includeExtendedResults, setIncludeExtendedResults] = useState(false);
  const [includeRelatedFeatures, setIncludeRelatedFeatures] = useState(false);
  const [displayMode, setDisplayMode] = useState<PromoByToggleDisplayMode>('standard');
  const debouncedQuery = useDebounced(query.trim(), NAME_QUERY_DEBOUNCE_MS);
  const lockedName = codeName ?? voucherName;
  const isNameLocked = lockedName !== undefined;
  const lockedEntity = codeName !== undefined ? 'promo' : voucherName !== undefined ? 'voucher' : undefined;
  const selectedEntity = lockedEntity ?? modeSet ?? entity;
  const isLookupOnly = isNameLocked || modeSet !== undefined || toggleSet !== undefined;
  const showToggleSelector = !isLookupOnly || (isNameLocked && toggleSet === undefined);
  const selectedIncludeRelatedFeatures = includeRelatedFeaturesSet ?? includeRelatedFeatures;
  const selectedDisplayMode = displayModeSet ?? displayMode;
  const toggleOptions = selectedEntity === 'promo' ? promoMeta?.promo_toggles : promoMeta?.voucher_toggles;
  // Do not send a toggle that belongs to the other entity: the API rejects invalid
  // entity/toggle combinations, and the user must explicitly choose the first toggle.
  const selectedToggle = toggleSet ?? (toggleOptions?.some((option) => option.key === toggle) ? toggle : undefined);
  const lookupQuery = lockedName ?? (debouncedQuery || (hasFocusedNameQuery ? 'ALL_RESULTS' : ''));
  const request = useMemo<SearchPromosByToggleQuery | null>(() => {
    if (!isActive || !selectedToggle || !lookupQuery) {
      return null;
    }

    const relatedFeatures = selectedIncludeRelatedFeatures
      ? toggleSearchRelatedFeatures[selectedEntity].map((feature) => feature.value)
      : undefined;

    if (relatedFeatures) {
      return {
        entity: selectedEntity,
        toggle: selectedToggle,
        query: lookupQuery,
        brand,
        ...(includeExtendedResults ? { limit: 50 } : {}),
        'is-related-to': relatedFeatures,
      };
    }

    return {
      entity: selectedEntity,
      toggle: selectedToggle,
      query: lookupQuery,
      brand,
      ...(includeExtendedResults ? { limit: 50 } : {}),
    };
  }, [
    brand,
    includeExtendedResults,
    isActive,
    lookupQuery,
    selectedEntity,
    selectedIncludeRelatedFeatures,
    selectedToggle,
  ]);
  const { data, error, isFetching } = useSearchPromosByToggle(request);

  useEffect(() => {
    onSearchChange?.();
  }, [onSearchChange, request]);

  useEffect(() => {
    if (data) {
      onSearchChange?.({ entity: selectedEntity, results: data.result, displayMode: selectedDisplayMode });
    }
  }, [data, onSearchChange, selectedDisplayMode, selectedEntity]);

  function handleEntityChange(nextEntity: PromoByToggleEntity) {
    setEntity(nextEntity);
  }

  function handleToggleChange(nextToggle: string) {
    const matchingToggle = toggleOptions?.find((option) => option.key === nextToggle);
    if (matchingToggle) {
      setToggle(matchingToggle.key);
    }
  }

  return (
    <Box mt={4}>
      <Stack spacing={2}>
        {!isLookupOnly && (
          <Box>
            <Typography variant="h6">Promo and voucher toggle search</Typography>
            <Typography variant="body2" color="text.secondary">
              Find promos or vouchers using a configured toggle.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          {!isLookupOnly && (
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                select
                label="Entity"
                value={selectedEntity}
                onChange={(event) => {
                  if (event.target.value === 'promo' || event.target.value === 'voucher') {
                    handleEntityChange(event.target.value);
                  }
                }}
                fullWidth
              >
                <MenuItem value="promo">Promo</MenuItem>
                <MenuItem value="voucher">Voucher</MenuItem>
              </TextField>
            </Grid>
          )}
          {showToggleSelector && (
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Toggle"
                value={selectedToggle ?? ''}
                onChange={(event) => handleToggleChange(event.target.value)}
                SelectProps={{ displayEmpty: true }}
                fullWidth
              >
                <MenuItem value="" disabled>
                  Select a toggle
                </MenuItem>
                {toggleOptions?.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.key}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {!isNameLocked && (
            <Grid size={{ xs: 12, md: isLookupOnly ? 8 : 4 }}>
              <TextField
                label="Name query"
                value={query}
                onFocus={() => setHasFocusedNameQuery(true)}
                onChange={(event) => setQuery(event.target.value)}
                fullWidth
              />
            </Grid>
          )}
          {!displayModeSet && (
            <Grid size={{ xs: 12, md: isLookupOnly ? 4 : 3 }}>
              <TextField
                select
                label="Display mode"
                value={selectedDisplayMode}
                onChange={(event) => {
                  if (event.target.value === 'standard' || event.target.value === 'refined') {
                    setDisplayMode(event.target.value);
                  }
                }}
                fullWidth
              >
                <MenuItem value="standard">Standard</MenuItem>
                <MenuItem value="refined">Refined</MenuItem>
              </TextField>
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeExtendedResults}
                    onChange={(event) => setIncludeExtendedResults(event.target.checked)}
                  />
                }
                label="Extended results (up to 50)"
              />
              {includeRelatedFeaturesSet === undefined && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedIncludeRelatedFeatures}
                      onChange={(event) => setIncludeRelatedFeatures(event.target.checked)}
                    />
                  }
                  label="Include related features"
                />
              )}
            </Stack>
          </Grid>
        </Grid>
        {isFetching && <CircularProgress size={24} aria-label="Searching promos and vouchers" />}
        {error && <Alert severity="error">{getErrorMessage(error)}</Alert>}
      </Stack>
    </Box>
  );
}
